import React, {Fragment, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useRouteMatch} from 'react-router-dom'
import Table from '@material-ui/core/Table'
import {Button, makeStyles, TableBody, TableRow} from '@material-ui/core'
import TableCell from 'components/Table/TableCell'
import {
  KeyPrices,
  TARGET_NATION,
  UNIT_PRICES,
} from 'pages/ProductsPages/constant'
import ProductsDetailStyle from 'pages/ProductsPages/ProductsDetail/Style'
import {AdminProductParamResponse} from 'api/productApi'
import {FieldErrors} from 'react-hook-form'
import {ProductSchema} from 'pages/ProductsPages/ProductsDetail'
import {formatPriceNumber} from 'helpers/uiHelper'
import {Close} from '@material-ui/icons'

const useStyles = makeStyles(ProductsDetailStyle)

interface TotalPriceProps {
  data: AdminProductParamResponse
  disabledAccordingPaymentMethod: boolean
  disabledAll?: boolean
  errors: FieldErrors<ProductSchema>
  setPrices: (value: string, key: KeyPrices) => void
  setSalePrices: (value: string, key: KeyPrices) => void
  setDescription: (value: string, key: string) => void
  setDiscountRate: (value: number) => void
  setNameProduct: (value: string, key: string) => void
  countAmount: () => void
  isAllRegion: boolean
  attachmentGood: {
    fileName: string
    fileKey: string
    eTag: string
  }
  onUploadingFile: (event: React.ChangeEvent<HTMLInputElement>) => void
  isShowAttachmentUploadBtn: boolean
  isShowOldAttachment: boolean
  oldAttachmentName: string
  onRemoveAttachment: () => void
}

const TotalPrice = ({
  data,
  disabledAccordingPaymentMethod,
  disabledAll = false,
  errors,
  setPrices,
  setSalePrices,
  setDescription,
  setDiscountRate,
  countAmount,
  setNameProduct,
  isAllRegion,
  attachmentGood,
  onUploadingFile,
  isShowAttachmentUploadBtn,
  isShowOldAttachment,
  oldAttachmentName,
  onRemoveAttachment,
}: TotalPriceProps) => {
  const {t} = useTranslation()
  const classes = useStyles()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    wonSale,
    dollarSale,
    wonTotal,
    dollarTotal,
    descKo,
    descEn,
    nameProductKo,
    nameProductEn,
  } = useMemo(() => {
    let won = 0
    let dollar = 0
    let wonPrice = 0
    let dollarPrice = 0
    let koName = ''
    let koDesc = ''
    let enName = ''
    let enDesc = ''

    data.sale.prices.forEach((element) => {
      if (element.currency === UNIT_PRICES.KRW) {
        won = element.price
      } else if (element.currency === UNIT_PRICES.USD) {
        dollar = element.price
      }
    })
    data.sale.salePrices.forEach((element) => {
      if (element.currency === UNIT_PRICES.KRW) {
        wonPrice = element.price
      } else if (element.currency === UNIT_PRICES.USD) {
        dollarPrice = element.price
      }
    })
    data.goods.displayInfos.forEach((item) => {
      if (item.langCode === 'ko') {
        koName = item.name
        koDesc = item.description
      }
      if (item.langCode === 'en') {
        enName = item.name
        enDesc = item.description
      }
    })
    return {
      wonSale: formatPriceNumber(wonPrice),
      dollarSale: formatPriceNumber(dollarPrice),
      wonTotal: formatPriceNumber(won),
      dollarTotal: formatPriceNumber(dollar),
      descKo: koDesc,
      descEn: enDesc,
      nameProductKo: koName,
      nameProductEn: enName,
    }
  }, [data])

  const selectedRegion = data.sale.saleTarget?.targets.find(
    (target) => target.targetType === 'Region',
  )?.targetId

  const isDisplayDomesticRegion =
    selectedRegion === TARGET_NATION.DOMESTIC || isAllRegion

  const isDisplayOverseasRegion =
    selectedRegion === TARGET_NATION.OVERSEAS || isAllRegion

  const onChangePrice = (value: string, currency: KeyPrices) => {
    const digitsOnly = value.replace(/[^0-9]/g, '')
    setPrices(digitsOnly, currency)
  }

  const onChangeSalePrice = (value: string, currency: KeyPrices) => {
    const digitsOnly = value.replace(/[^0-9]/g, '')
    setSalePrices(digitsOnly, currency)
  }

  return (
    <Fragment>
      <Table size='small' className={classes.table}>
        <TableBody>
          {isDisplayDomesticRegion && (
            <TableRow>
              <TableCell
                align='center'
                className={`${classes.cellParent}`}
                style={{
                  width: '160px',
                }}
              >
                {t('IPackageNameKR')}
              </TableCell>

              <TableCell
                align='left'
                className={classes.headerMax}
                style={{
                  width: 562,
                }}
              >
                <input
                  className={classes.input}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                  }}
                  type='text'
                  value={nameProductKo}
                  onChange={(e) => setNameProduct(e.target.value, 'ko')}
                  disabled={disabledAll}
                />
                {errors.nameKo?.message && (
                  <p style={{color: 'red'}}>{errors.nameKo.message}</p>
                )}
              </TableCell>
              <TableCell
                align='center'
                className={`${classes.cellParent}`}
                style={{
                  width: '160px',
                }}
              >
                {t('IPackageDescriptionKR')}
              </TableCell>

              <TableCell
                align='center'
                className={classes.headerMax}
                style={{
                  width: 562,
                }}
              >
                <div className={classes.itemCell}>
                  <textarea
                    className={classes.inputV3}
                    value={descKo}
                    onChange={(e) => setDescription(e.target.value, 'ko')}
                    disabled={disabledAll}
                  />
                </div>
              </TableCell>
            </TableRow>
          )}
          {isDisplayOverseasRegion && (
            <TableRow>
              <TableCell
                align='center'
                className={`${classes.cellParent}`}
                style={{
                  width: '160px',
                }}
              >
                {t('IPackageNameEN')}
              </TableCell>

              <TableCell
                align='left'
                className={classes.headerMax}
                style={{
                  width: 562,
                }}
              >
                <input
                  className={classes.input}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                  }}
                  type='text'
                  value={nameProductEn}
                  onChange={(e) => setNameProduct(e.target.value, 'en')}
                  disabled={disabledAll}
                />
                {errors.nameEn?.message && (
                  <p style={{color: 'red'}}>{errors.nameEn.message}</p>
                )}
              </TableCell>
              <TableCell
                align='center'
                className={`${classes.cellParent}`}
                style={{
                  width: '160px',
                }}
              >
                {t('IPackageDescriptionEN')}
              </TableCell>

              <TableCell
                align='center'
                className={classes.headerMax}
                style={{
                  width: 562,
                }}
              >
                <div className={classes.itemCell}>
                  <textarea
                    className={classes.inputV3}
                    value={descEn}
                    onChange={(e) => setDescription(e.target.value, 'en')}
                    disabled={disabledAll}
                  />
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Table size='small' className={classes.table}>
        <TableBody>
          {isDisplayDomesticRegion && (
            <TableRow>
              <TableCell
                align='center'
                className={`${classes.cellParent}`}
                style={{
                  width: '160px',
                }}
              >
                {t('IOriginalPrice')} (₩)
              </TableCell>

              <TableCell
                align='center'
                className={classes.headerMax}
                style={{
                  width: 562,
                }}
              >
                <div className={classes.itemCell}>
                  <input
                    type='text'
                    className={classes.inputNumber}
                    value={wonTotal}
                    disabled={disabledAccordingPaymentMethod || disabledAll}
                    onChange={(e) =>
                      onChangePrice(e.target.value, UNIT_PRICES.KRW)
                    }
                    min={0}
                  />
                </div>
              </TableCell>
              <TableCell
                align='center'
                className={`${classes.cellParent}`}
                style={{
                  width: '160px',
                }}
              >
                {t('ISalesPrice')} (₩)
              </TableCell>
              <TableCell
                align='center'
                className={classes.headerMax}
                style={{
                  width: 562,
                }}
              >
                <div className={classes.itemCell}>
                  <input
                    type='text'
                    min={0}
                    className={classes.inputNumber}
                    value={wonSale}
                    disabled={disabledAccordingPaymentMethod || disabledAll}
                    onChange={(e) =>
                      onChangeSalePrice(e.target.value, UNIT_PRICES.KRW)
                    }
                  />
                </div>
              </TableCell>
            </TableRow>
          )}
          {isDisplayOverseasRegion && (
            <TableRow>
              <TableCell
                align='center'
                className={`${classes.cellParent}`}
                style={{
                  width: '160px',
                }}
              >
                {t('IOriginalPrice')} ($)
              </TableCell>

              <TableCell
                align='center'
                className={classes.headerMax}
                style={{
                  width: 562,
                }}
              >
                <div className={classes.itemCell} style={{gap: '10px'}}>
                  <input
                    type='text'
                    className={classes.inputNumber}
                    value={dollarTotal}
                    disabled={disabledAccordingPaymentMethod || disabledAll}
                    onChange={(e) =>
                      onChangePrice(e.target.value, UNIT_PRICES.USD)
                    }
                    min={0}
                  />
                </div>
              </TableCell>
              <TableCell
                align='center'
                className={`${classes.cellParent}`}
                style={{
                  width: '160px',
                }}
              >
                {t('ISalesPrice')} ($)
              </TableCell>

              <TableCell
                align='center'
                className={classes.headerMax}
                style={{
                  width: 562,
                }}
              >
                <div className={classes.itemCell}>
                  <input
                    type='text'
                    min={0}
                    className={classes.inputNumber}
                    value={dollarSale}
                    disabled={disabledAccordingPaymentMethod || disabledAll}
                    onChange={(e) =>
                      onChangeSalePrice(e.target.value, UNIT_PRICES.USD)
                    }
                  />
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Table>
        <TableBody>
          <TableRow>
            <TableCell
              align='center'
              className={`${classes.cellParent}`}
              style={{
                width: '160px',
              }}
            >
              {t('IAttachFile')}
            </TableCell>

            <TableCell
              align='center'
              className={classes.headerMax}
              style={{
                width: 562,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <input
                    type='text'
                    className={classes.inputV2}
                    style={{width: '60%', textAlign: 'left'}}
                    value={attachmentGood.fileName}
                    disabled
                    readOnly
                  />
                  {isShowAttachmentUploadBtn && (
                    <Button
                      color='primary'
                      variant='contained'
                      className={classes.h30}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {t('IFile')}
                    </Button>
                  )}
                  <input
                    ref={fileInputRef}
                    type='file'
                    hidden
                    onChange={(e) => onUploadingFile(e)}
                  />
                </div>
                {isShowOldAttachment && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 16,
                      }}
                    >
                      {oldAttachmentName}
                    </div>
                    <Close
                      style={{
                        cursor: 'pointer',
                        color: '#ff5252',
                      }}
                      onClick={onRemoveAttachment}
                    />
                  </div>
                )}
              </div>
            </TableCell>
            <TableCell
              align='center'
              className={`${classes.cellParent}`}
              style={{
                width: '160px',
              }}
            >
              {t('IDiscountRate')}
            </TableCell>
            <TableCell
              align='center'
              className={classes.headerMax}
              style={{
                width: 562,
              }}
            >
              <div className={classes.itemCell} style={{gap: '10px'}}>
                <input
                  type='number'
                  className={classes.inputV2}
                  style={{width: '80px'}}
                  value={data.sale.discountRate}
                  disabled={disabledAccordingPaymentMethod || disabledAll}
                  onChange={(e) => setDiscountRate(Number(e.target.value))}
                  min={0}
                />
                <Button
                  color='primary'
                  variant='contained'
                  className={classes.h30}
                  disabled={disabledAccordingPaymentMethod || disabledAll}
                  onClick={countAmount}
                >
                  {t('IApply')}
                </Button>
              </div>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Fragment>
  )
}

export default TotalPrice
